import moment from "moment";
import {
  DynamicContentAttributeOperator,
  GlobalDispatchMethods,
} from "../../../js/enums";
import { DispatchMethods } from "../enums";
import { DesignMode, DragType, LinkType } from "./enums";
import {
  BookingControlDefaultValues,
  BookingControlFormStages,
  ButtonControlDefaultValues,
  ButtonControlFormStages,
  DividerControlDefaultValues,
  DividerControlFormStages,
  ImageControlDefaultValues,
  ImageControlFormStages,
  ImageTextControlDefaultValues,
  ImageTextControlFormStages,
  MembershipRenewalControlDefaultValues,
  MembershipRenewalControlFormStages,
  SocialFollowControlDefaultValues,
  GetSocialFollowControlFormStages,
  TextControlDefaultValues,
  TextControlFormStages,
  CouponControlFormStages,
  VoucherControlFormStages,
  CouponControlDefaultValues,
  VoucherControlDefaultValues,
} from "./controls";

const updateDynamicContentItems = (
  control,
  dynamicContentItemArray
) => {
  let result = [];

  if (
    !dynamicContentItemArray ||
    dynamicContentItemArray.length === 0
  ) {
    return result;
  }

  dynamicContentItemArray.forEach((d) => {
    result.push({
      AttributeOperator: d.AttributeOperator,
      AttributeValue1: d.AttributeValue1,
      AttributeValue1Caption: d.AttributeValue1Caption,
      AttributeValue1Id: d.AttributeValue1Id,
      AttributeValue1Guid: d.AttributeValue1Guid,
      AttributeValue2: d.AttributeValue2,
      UUID: d.UUID,
      Properties: control.Properties.map((property) => {
        return {
          Name: property.Name,
          Value: d[property.Name],
        };
      }),
    });
  });

  return result;
};

const applyControlChanges = (dispatch, values) => {
  dispatch({
    type: DispatchMethods.SetReloadValues,
    reloadValues: {
      ...values,
      rows: values.rows.map((row, rowIndex) => {
        return {
          ...row,
          Columns: row.Columns.map((column, columnIndex) => {
            return {
              ...column,
              Controls: column.Controls.map(
                (control, controlIndex) => {
                  if (
                    rowIndex === values.editControl.rowIndex &&
                    columnIndex === values.editControl.columnIndex &&
                    controlIndex === values.editControl.controlIndex
                  ) {
                    return {
                      Name: values.controlName,
                      Template: control.Template,
                      UUID: control.UUID,
                      Properties: control.Properties.map(
                        (property) => {
                          return {
                            Name: property.Name,
                            Value: values[property.Name],
                          };
                        }
                      ),
                      Attribute: values.attribute,
                      AttributeName: values.attributeName,
                      AttributeTarget: values.attributeTarget,
                      AttributeType: values.attributeType,
                      DynamicContentItems: updateDynamicContentItems(
                        control,
                        values.dynamicContentItemsValuesArray
                      ),
                      Entity: values.entity,
                      HideDefaultContent: values.hideDefaultContent,
                    };
                  } else {
                    return control;
                  }
                }
              ),
            };
          }),
        };
      }),
      editControl: "",
    },
  });
};

const cancelControlChanges = (dispatch, values) => {
  dispatch({
    type: DispatchMethods.SetReloadValues,
    reloadValues: {
      designMode: String(DesignMode.Controls),
      editControl: "",
      globalBackgroundColour: values.globalBackgroundColour,
      globalContentBackgroundColour:
        values.globalContentBackgroundColour,
      globalContentPadding: values.globalContentPadding,
      globalLinkColour: values.globalLinkColour,
      globalPadding: values.globalPadding,
      rows: values.rows,
    },
  });
};

const changeDesignMode = (dispatch, designMode, values) => {
  dispatch({
    type: DispatchMethods.SetReloadValues,
    reloadValues: {
      ...values,
      designMode: String(designMode),
    },
  });
};

const checkIfDynamicContentItemSelected = (
  selectedDynamicContentItemIndex
) => {
  return (
    selectedDynamicContentItemIndex !== "" &&
    selectedDynamicContentItemIndex !== "default" &&
    selectedDynamicContentItemIndex >= 0
  );
};

const convertTypeNumberToEntityDisplayName = (typeNumber) => {
  switch (String(typeNumber)) {
    case "1":
      return "Booking";
    case "2":
      return "Booking Payment";
    case "3":
      return "Contact";
    case "4":
    case "5":
      return "Membership";
    case "6":
      return "Account";
    case "7":
      return "Coupon";
    case "8":
      return "Voucher";
    default:
      return "";
  }
};

const deleteControl = (
  columnIndex,
  controlIndex,
  globalDispatch,
  rowIndex,
  setFieldValue,
  values
) => {
  // prevent if a control is being edited
  if (values.editControl) {
    globalDispatch({
      type: GlobalDispatchMethods.AddNotification,
      notification: {
        message: "Cannot delete while a component is being edited",
        success: false,
      },
      dispatch: globalDispatch,
    });
    return;
  }

  setFieldValue(
    "rows",
    values.rows.map((row, i) => {
      return {
        ...row,
        Columns: row.Columns.map((column, j) => {
          return {
            ...column,
            Controls: column.Controls.filter(
              (_, k) =>
                !(
                  i === rowIndex &&
                  j === columnIndex &&
                  k === controlIndex
                )
            ),
          };
        }),
      };
    })
  );
};

const deleteRow = (rowIndex, setFieldValue, values) => {
  setFieldValue(
    "rows",
    values.rows.filter((_, i) => i !== rowIndex)
  );
};

const getElementOffset = (element) => {
  let topDistance = 0;
  let leftDistance = 0;

  while (element) {
    topDistance += element.offsetTop;
    leftDistance += element.offsetLeft;
    element = element.offsetParent;
  }

  return { top: topDistance, left: leftDistance };
};

const getEmailLinkId = (href) => {
  let url = "";
  if (href.startsWith("..")) {
    url = new URL("https://www.green4solutions.com/" + href);
  } else if (href.indexOf("actions/redirect.aspx?url=") > -1) {
    const parentUrl = new URL(href);
    let redirectUrl = parentUrl.searchParams.get("url");
    if (redirectUrl.startsWith("..")) {
      redirectUrl = new URL(
        redirectUrl,
        parentUrl.origin + parentUrl.pathname
      ).href;
    }
    url = new URL(redirectUrl);
  } else {
    url = new URL(href);
  }

  const id = new URL(url).searchParams.get("g4id");
  return id ? parseInt(id) : null;
};

const getLinkHref = (properties, prefix) => {
  const linkType =
    properties[prefix ? `${prefix}LinkType` : "linkType"];

  switch (linkType) {
    case LinkType.Predefined:
      return properties[
        prefix ? `${prefix}PredefinedLinks` : "predefinedLinks"
      ];
    case LinkType["Email Address"]:
      const emailAddress =
        properties[prefix ? `${prefix}EmailAddress` : "emailAddress"];
      const subject = encodeURIComponent(
        properties[prefix ? `${prefix}EmailSubject` : "emailSubject"]
      );
      const content = encodeURIComponent(
        properties[prefix ? `${prefix}EmailContent` : "emailContent"]
      );
      return `mailto:${emailAddress}?subject=${subject}&body=${content}`;
    case LinkType.Attribute:
      const attribute =
        properties[
          prefix
            ? `${prefix}SelectedLinkAttribute`
            : "selectedLinkAttribute"
        ];
      return `actions/redirect.aspx?url=$$attribute:${attribute}-$`;
    case LinkType["Web Address"]:
    default:
      return properties[prefix ? `${prefix}Link` : "link"];
  }
};

const getPredefinedAttributes = (selectedEntity) => {
  switch (String(selectedEntity)) {
    case "1": //Booking
      return Object.freeze({
        "Booking Reference": "g4b_bookingreference",
        "Total Price": "g4b_totalprice",
      });
    case "2": //Booking Payment
      return Object.freeze({
        "Payment Amount": "g4b_paymentamount",
        "Payment URL": "g4b_paymenturl",
      });
    case "3": //Contact
      return Object.freeze({
        Email: "emailaddress1",
        "First Name": "firstname",
        "Full Name": "fullname",
        "Last Name": "lastname",
        Salutation: "salutation",
      });
    case "4": //Membership (Member)
    case "5": //Membership (Owner)
      return Object.freeze({
        "End Date": "g4m_enddate",
        "Membership Number": "g4m_membershipnumber",
        "Renewal Date": "g4m_renewaldate",
        "Start Date": "g4m_startdate",
      });
    case "6": //Account
      return Object.freeze({
        "Account Name": "name",
      });
    case "7": //Coupon
      return Object.freeze({
        Code: "g4b_couponcode",
        "Full Name": "g4b_customername",
      });
    case "8": //Voucher
      return Object.freeze({
        "Full Name": "g4b_customername",
        "Reference Number": "g4b_referencenumber",
      });
    default:
      return "";
  }
};

const getPredefinedAttributeDataTypeCodes = (selectedEntity) => {
  switch (String(selectedEntity)) {
    case "1": //Booking
      return Object.freeze({
        g4b_bookingreference: 14,
        g4b_totalprice: 8,
      });
    case "2": //Booking Payment
      return Object.freeze({
        g4b_paymentamount: 8,
        g4b_paymenturl: 14,
      });
    case "3": //Contact
      return Object.freeze({
        emailaddress1: 14,
        firstname: 14,
        fullname: 14,
        lastname: 14,
        salutation: 14,
      });
    case "4": //Membership (Member)
    case "5": //Membership (Owner)
      return Object.freeze({
        g4m_enddate: 2,
        g4m_membershipnumber: 14,
        g4m_renewaldate: 2,
        g4m_startdate: 2,
      });
    case "6": //Account
      return Object.freeze({
        name: 14,
      });
    case "7": //Coupon
      return Object.freeze({
        g4b_couponcode: 14,
        g4b_customername: 14,
      });
    case "8": //Voucher
      return Object.freeze({
        g4b_customername: 14,
        g4b_referencenumber: 14,
      });
    default:
      return "";
  }
};

const getPredefinedAttributesForDynamicContent = (selectedEntity) => {
  switch (String(selectedEntity)) {
    case "1": //Booking
      return Object.freeze({
        "Booking Status": "g4b_bookingstatus",
        Channel: "g4b_channelid",
        "Contact Has Password": "g4b_contacthaspassword",
        "Has Active Declaration": "g4d_hasactivedeclaration",
        "Has Unassigned Required Beneficiaries":
          "g4b_hasunassignedrequiredbeneficiaries",
        "Is Guest Checkout": "g4b_isguestcheckout",
        "Total Price": "g4b_totalprice",
      });
    case "2": //Booking Payment
      return Object.freeze({
        "Payment Amount": "g4b_paymentamount",
        "Payment Method": "g4b_paymentmethodid",
        Terminal: "g4b_terminalid",
      });
    case "3": //Contact
      return Object.freeze({
        Age: "g4_age",
        Gender: "gendercode",
        "Is Guest Checkout": "g4b_isguestcheckout",
      });
    case "4": //Membership (Member)
    case "5": //Membership (Owner)
      return Object.freeze({
        "Bookable Product": "g4m_productid",
        "Variant Type": "g4m_varianttypeid",
      });
    case "6": //Account
      return Object.freeze({
        "Credit Account": "g4b_creditaccount",
      });
    case "7": //Coupon
      return Object.freeze({
        "Coupon Status": "g4b_couponstatus",
        "Coupon Type": "g4b_coupontypeid",
      });
    case "8": //Voucher
      return Object.freeze({
        Value: "g4b_value",
        "Voucher Status": "g4b_voucherstatus",
      });
    default:
      return "";
  }
};

const getPredefinedAttributeLookupEntityForDynamicContent = (
  selectedEntity
) => {
  switch (String(selectedEntity)) {
    case "1": //Booking
      return Object.freeze({
        g4b_channelid: "g4b_channel",
      });
    case "2": //Booking Payment
      return Object.freeze({
        g4b_paymentmethodid: "g4b_paymentmethod",
        g4b_terminalid: "g4b_terminal",
      });
    case "4": //Membership (Member)
    case "5": //Membership (Owner)
      return Object.freeze({
        g4m_productid: "g4b_productbase",
        g4m_varianttypeid: "g4b_varianttype",
      });
    case "7": //Coupon
      return Object.freeze({
        g4b_coupontypeid: "g4b_coupontype",
      });
    default:
      return "";
  }
};

const getPredefinedAttributeDataTypeCodesForDynamicContent = (
  selectedEntity
) => {
  switch (String(selectedEntity)) {
    case "1": //Booking
      return Object.freeze({
        g4b_bookingstatus: 11,
        g4b_channelid: 6,
        g4b_contacthaspassword: 0,
        g4d_hasactivedeclaration: 0,
        g4b_hasunassignedrequiredbeneficiaries: 0,
        g4b_isguestcheckout: 0,
        g4b_totalprice: 8,
      });
    case "2": //Booking Payment
      return Object.freeze({
        g4b_paymentamount: 8,
        g4b_paymentmethodid: 6,
        g4b_terminalid: 6,
      });
    case "3": //Contact
      return Object.freeze({
        g4_age: 5,
        g4b_isguestcheckout: 0,
        gendercode: 11,
      });
    case "4": //Membership (Member)
    case "5": //Membership (Owner)
      return Object.freeze({
        g4m_productid: 6,
        g4m_varianttypeid: 6,
      });
    case "6": //Account
      return Object.freeze({
        g4b_creditaccount: 0,
      });
    case "7": //Coupon
      return Object.freeze({
        g4b_couponstatus: 11,
        g4b_coupontypeid: 6,
      });
    case "8": //Voucher
      return Object.freeze({
        g4b_value: 8,
        g4b_voucherstatus: 11,
      });
    default:
      return "";
  }
};

const getStagesForControl = (template, properties) => {
  switch (template) {
    case "booking-control":
      return BookingControlFormStages;
    case "button-control":
      return ButtonControlFormStages;
    case "coupon-control":
      return CouponControlFormStages;
    case "divider-control":
      return DividerControlFormStages;
    case "image-control":
      return ImageControlFormStages;
    case "image-text-control":
      return ImageTextControlFormStages;
    case "membership-renewal-control":
      return MembershipRenewalControlFormStages;
    case "social-follow-control":
      return GetSocialFollowControlFormStages(properties);
    case "voucher-control":
      return VoucherControlFormStages;
    default:
      return TextControlFormStages;
  }
};

const handleDragEnd = (
  event,
  saveUndoSnapshot,
  setFieldValue,
  values
) => {
  const { active, over } = event;

  if (!active || !over) {
    return;
  }

  const dragType = getDragType(active.id, over.id);
  if (dragType === null) {
    return;
  }

  // deep copy the rows to avoid changing the original value and affecting snapshots
  let rows = [];
  const rowCopy = JSON.parse(JSON.stringify(values.rows));

  switch (dragType) {
    case DragType.NewRow:
      rows = handleNewRow(rowCopy, active.id, over.id);
      break;
    case DragType.MoveRow:
      rows = handleMoveRow(rowCopy, active.id, over.id);
      break;
    case DragType.NewControl:
      rows = handleNewControl(rowCopy, active.id, over.id);
      break;
    case DragType.MoveControl:
      rows = handleMoveControl(rowCopy, active.id, over.id);
      break;
    default:
      break;
  }

  if (rows === null || rows.length === 0) {
    return;
  }

  // update the rows value
  saveUndoSnapshot(values);
  setFieldValue("rows", rows);
};

const getDragType = (activeId, overId) => {
  if (overId.indexOf("row") === 0) {
    return activeId.indexOf("moverow") === 0
      ? DragType.MoveRow
      : DragType.NewRow;
  }

  if (overId.indexOf("control") === 0) {
    return activeId.indexOf("movecontrol") === 0
      ? DragType.MoveControl
      : DragType.NewControl;
  }

  return null;
};

const getDynamicContentTitle = (
  attributeOperator,
  attributeValue1,
  attributeValue1Caption,
  attributeValue2
) => {
  return `${attributeOperator} ${
    attributeValue1Caption ? attributeValue1Caption : attributeValue1
  } ${attributeOperator === "Between" ? "& " + attributeValue2 : ""}`;
};

const handleNewRow = (result, activeId, overId) => {
  // determine the indexes
  const overIdentifiers = overId.split("-");
  if (overIdentifiers.length !== 2) {
    return null;
  }

  const index = parseInt(overIdentifiers[1]);

  // determine the number of columns to add
  const activeIdentifiers = activeId.split("-");
  const columns = parseInt(activeIdentifiers[0]);

  // add the new row
  result.splice(index, 0, {
    Columns: new Array(columns).fill().map(() => ({ Controls: [] })),
  });

  return result;
};

const handleMoveRow = (result, activeId, overId) => {
  // determine the indexes
  const overIdentifiers = overId.split("-");
  if (overIdentifiers.length !== 2) {
    return null;
  }

  const index = parseInt(overIdentifiers[1]);

  // row being moved
  const activeIdentifiers = activeId.split("-");
  const rowIndex = parseInt(activeIdentifiers[1]);

  // remember the row being removed
  const row = result[rowIndex];

  // remove the row
  result.splice(rowIndex, 1);

  // add the row back in
  const newRowIndex = rowIndex < index ? index - 1 : index;
  result.splice(newRowIndex, 0, row);

  return result;
};

const generateUUID = () => {
  var d = new Date().getTime();
  var uuid = "xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
};

const handleNewControl = (result, activeId, overId) => {
  // determine the indexes
  const overIdentifiers = overId.split("-");
  if (overIdentifiers.length !== 4) {
    return;
  }

  const rowIndex = parseInt(overIdentifiers[1]);
  const columnIndex = parseInt(overIdentifiers[2]);
  const controlIndex = parseInt(overIdentifiers[3]);

  if (
    result[rowIndex] &&
    result[rowIndex].Columns &&
    result[rowIndex].Columns[columnIndex] &&
    result[rowIndex].Columns[columnIndex].Controls
  ) {
    result[rowIndex].Columns[columnIndex].Controls.splice(
      controlIndex,
      0,
      {
        Name: getNameForTemplate(activeId),
        Properties: getDefaultPropertiesForTemplate(activeId),
        UUID: generateUUID(),
        Template: activeId,
        Attribute: null,
        AttributeName: null,
        AttributeTarget: null,
        AttributeType: null,
        DynamicContentItems: [],
        Entity: null,
        HideDefaultContent: false,
      }
    );
  }

  return result;
};

const handleMoveControl = (result, activeId, overId) => {
  // determine the indexes
  const overIdentifiers = overId.split("-");
  const activeIdentifiers = activeId.split("-");
  if (
    overIdentifiers.length !== 4 ||
    activeIdentifiers.length !== 4
  ) {
    return;
  }

  const rowIndex = parseInt(overIdentifiers[1]);
  const columnIndex = parseInt(overIdentifiers[2]);
  let controlIndex = parseInt(overIdentifiers[3]);

  const activeRowIndex = parseInt(activeIdentifiers[1]);
  const activeColumnIndex = parseInt(activeIdentifiers[2]);
  const activeControlIndex = parseInt(activeIdentifiers[3]);

  // remember the control being removed
  const control =
    result[activeRowIndex].Columns[activeColumnIndex].Controls[
      activeControlIndex
    ];

  // remove the control
  result[activeRowIndex].Columns[activeColumnIndex].Controls.splice(
    activeControlIndex,
    1
  );

  if (
    rowIndex === activeRowIndex &&
    columnIndex === activeColumnIndex &&
    controlIndex > activeControlIndex
  ) {
    // adjust the new control index as we are moving the control within the same row and column
    controlIndex--;
  }

  // add the control back in
  result[rowIndex].Columns[columnIndex].Controls.splice(
    controlIndex,
    0,
    control
  );

  return result;
};

const getAttributeOperatorEnum = (attributeType) => {
  let allOperators = Object.entries(
    DynamicContentAttributeOperator
  ).map(([Name, Number]) => ({
    Key: Number,
    Value: Name,
  }));

  let result = [];
  allOperators.forEach((o) => {
    switch (o.Key) {
      case "<":
      case ">":
      case "<=":
      case ">=":
      case "Between":
        if (
          attributeType === 3 ||
          attributeType === 4 ||
          attributeType === 5 ||
          attributeType === 8 ||
          attributeType === 18 ||
          attributeType === 2
        ) {
          result.push(o);
        }
        break;
      case "StartsWith":
      case "EndsWith":
        if (
          attributeType === 6 ||
          attributeType === 7 ||
          attributeType === 14
        ) {
          result.push(o);
        }
        break;
      case "In":
      case "NotIn":
        if (attributeType === 7 || attributeType === 14) {
          result.push(o);
        }
        break;
      default:
        result.push(o);
        break;
    }
  });
  return result;
};

const getBoolValue = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === "1" || value === "true") {
    return true;
  }
  return false;
};

function generateCommunicationTemplateDataAndHTML(values) {
  let result = { templateData: "", templateHTML: null };
  let templateControls = {};
  let i = 1;
  values.rows.forEach((row) => {
    row.Columns.forEach((column) => {
      const zoneControls = [];
      column.Controls.forEach((control) => {
        zoneControls.push({
          Name: control.Name,
          Template: control.Template,
          UUID: control.UUID,
          Properties:
            control.Properties && control.Properties.length > 0
              ? control.Properties.map((property) => ({
                  Name: property.Name,
                  Value: property.Value,
                }))
              : [],
          Settings: [],
          Attribute: control.Attribute,
          Attributes: [],
          AttributeName: control.AttributeName,
          AttributeType: control.AttributeType,
          AttributeOperator: control.AttributeOperator,
          AttributeTarget: control.AttributeTarget,
          AttributeOptionSet: [],
          DynamicContentItems:
            control.DynamicContentItems &&
            control.DynamicContentItems.length > 0
              ? control.DynamicContentItems.map((d) => {
                  //Setting the following as empty arrays
                  //so that the record can be loaded up in CRM
                  d.Settings = [];
                  d.DynamicContentItems = [];
                  d.AttributeOptionSet = [];
                  d.Attributes = [];
                  d.Entities = [];
                  d.OriginalSettings = [];
                  d.OriginalProperties = [];
                  return d;
                })
              : [],
          Entity: control.Entity,
          Entities: [],
          HideDefaultContent: control.HideDefaultContent,
          OriginalSettings: [],
          OriginalProperties: [],
        });
      });

      templateControls[`zone-${i}`] = zoneControls;
      i++;
    });
  });

  result.templateData = window.btoa(
    encodeURIComponent(
      JSON.stringify({
        BackgroundColour: values.globalBackgroundColour,
        ContentBackgroundColour: values.globalContentBackgroundColour,
        ContentPadding: values.globalContentPadding,
        LinkColour: values.globalLinkColour,
        Padding: values.globalPadding,
        Rows: values.rows.map((row) => row.Columns.length),
        TemplateControls: templateControls,
      })
    )
  );

  const ink = document.getElementById("ink");

  if (ink) {
    const templateHtmlRaw = `<!DOCTYPE html>
    <html lang="en" xmlns="http://www.w3.org/1999/xhtml" class="email-html">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width" />
      <title></title>
      <link rel="stylesheet" href="ink.css" />
      ${
        values.globalLinkColour
          ? `<style>
              #ink a {
                color: ${values.globalLinkColour} !important;
              }
            </style>`
          : ""
      }
    </head>
    <body>${ink.outerHTML}</body>
    </html>`;

    const templateHtml = `<!DOCTYPE html>
    <html lang="en" xmlns="http://www.w3.org/1999/xhtml" class="email-html">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width" />
      <title></title>
      <style type="text/css" id="ink-css"></style>
      ${
        values.globalLinkColour
          ? `<style>
              #ink a {
                color: ${values.globalLinkColour} !important;
              }
            </style>`
          : ""
      }
    </head>
    <body>${ink.outerHTML}</body>
    </html>`;

    result.templateHTMLRaw = templateHtmlRaw;
    result.templateHTML = window.btoa(
      encodeURIComponent(templateHtml)
    );
  }
  return result;
}

const getDateString = (dayOffset, monthOffset, format) => {
  const currentDate = moment();

  // Apply the offsets to the current date
  currentDate.add(dayOffset, "days");
  currentDate.add(monthOffset, "months");

  return currentDate.format(format);
};

const getDefaultPropertiesForTemplate = (template) => {
  switch (template) {
    case "booking-control":
      return BookingControlDefaultValues;
    case "button-control":
      return ButtonControlDefaultValues;
    case "coupon-control":
      return CouponControlDefaultValues;
    case "divider-control":
      return DividerControlDefaultValues;
    case "image-control":
      return ImageControlDefaultValues;
    case "image-text-control":
      return ImageTextControlDefaultValues;
    case "membership-renewal-control":
      return MembershipRenewalControlDefaultValues;
    case "social-follow-control":
      return SocialFollowControlDefaultValues;
    case "voucher-control":
      return VoucherControlDefaultValues;
    default:
      return TextControlDefaultValues;
  }
};

const getNameForTemplate = (template) => {
  switch (template) {
    case "booking-control":
      return "Booking Details";
    case "button-control":
      return "Button";
    case "coupon-control":
      return "Coupon";
    case "divider-control":
      return "Divider";
    case "image-control":
      return "Image";
    case "image-text-control":
      return "Text & Image";
    case "membership-renewal-control":
      return "Membership Renewal";
    case "social-follow-control":
      return "Social Follow";
    case "voucher-control":
      return "Voucher";
    default:
      return "Text";
  }
};

const mapControlProperties = (properties, template) => {
  const stages = getStagesForControl(template, properties);

  const booleanFields = stages.flatMap((stage) =>
    stage.sections.flatMap((section) =>
      section.fields
        .filter((field) => field.type === "bit")
        .map((field) => field.name)
    )
  );

  booleanFields.forEach((field) => {
    properties[field] = getBoolValue(properties[field]);
  });

  return properties;
};

const renderHeatMap = (setHeatMapState, Statistics, UniqueClicks) => {
  const heatMap = document.getElementById("communication-heat-map");
  const body =
    heatMap && heatMap.contentDocument
      ? heatMap.contentDocument.body
      : null;
  if (!body) {
    return;
  }

  // set the height and then wait for update
  setHeatMapState((previous) => {
    return {
      anchors: [],
      height: body.scrollHeight,
      show: previous.show,
    };
  });

  setTimeout(function () {
    // remove mobile elements
    Array.from(body.getElementsByClassName("show-for-small")).forEach(
      function (element) {
        element.parentNode.removeChild(element);
      }
    );

    const { ClickThroughs } = Statistics;
    // get the click throughs and corresponding id
    const counts = ClickThroughs.map((x) => x.Count);
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);

    const heat1 = minCount;
    const increment = parseFloat((maxCount - minCount) / 3);
    const heat2 = heat1 + increment;
    const heat3 = heat2 + increment;

    for (const clickThrough of ClickThroughs) {
      clickThrough.Id = getEmailLinkId(clickThrough.Page);
      clickThrough.Heat =
        clickThrough.Count >= heat3
          ? 3
          : clickThrough.Count >= heat2
          ? 2
          : 1;
    }

    let heatMapAnchors = [];
    const anchors = body.getElementsByTagName("a");
    for (const anchor of anchors) {
      const offset = getElementOffset(anchor);

      const id = getEmailLinkId(anchor.href);

      const clickThrough = id
        ? ClickThroughs.find((x) => x.Id === id)
        : null;

      const count = clickThrough ? clickThrough.Count : 0;
      const totalCount = clickThrough ? clickThrough.TotalCount : 0;
      const percentage =
        UniqueClicks > 0 && count > 0
          ? (count * 100) / UniqueClicks
          : 0;
      const heat = clickThrough ? clickThrough.Heat : 0;

      heatMapAnchors.push({
        count: count,
        heat: heat,
        href: anchor.getAttribute("href"),
        id: id,
        offsetHeight: anchor.offsetHeight,
        offsetLeft: offset.left,
        offsetTop: offset.top,
        offsetWidth: anchor.offsetWidth,
        percentage: `${percentage.toFixed(2)}%`,
        totalCount: totalCount,
        uniqueClicks: 0,
      });
    }

    setHeatMapState((previous) => {
      return {
        anchors: heatMapAnchors,
        height: body.scrollHeight,
        show: previous.show,
      };
    });
  }, 100);
};

const setCommCopyAttributes = (attributes, state) => {
  let commTemplateDataAndHTML =
    generateCommunicationTemplateDataAndHTML(state);

  attributes["templateData"] = commTemplateDataAndHTML.templateData;
  attributes["templateHtml"] = commTemplateDataAndHTML.templateHTML;
  attributes["g4c_active"] = false;
  attributes["g4c_listsid"] = null;
  attributes["g4c_sid"] = null;
  attributes["g4c_publishedrecipients"] = null;
  attributes["statuscode"] = 1;
  if (state.g4c_designerdata) {
    // only reset the template if it was created in comm creator
    attributes["g4c_templateid"] = null;
  }
};

const setEditControl = (
  columnIndex,
  controlIndex,
  dispatch,
  globalDispatch,
  name,
  properties,
  rowIndex,
  template,
  values
) => {
  // prevent if a control is being edited
  if (values.editControl) {
    globalDispatch({
      type: GlobalDispatchMethods.AddNotification,
      notification: {
        message:
          "Cannot edit while another component is being edited",
        success: false,
      },
      dispatch: globalDispatch,
    });
    return;
  }

  const stages = getStagesForControl(template, properties);

  const control =
    values.rows[rowIndex].Columns[columnIndex].Controls[controlIndex];

  //Dynamic content
  const dynamicContentItems = control.DynamicContentItems;
  const dynamicContentItemsValuesArray = [];

  if (dynamicContentItems) {
    dynamicContentItems.forEach((d) => {
      let dynamicContentItem = {
        AttributeOperator: d.AttributeOperator,
        AttributeValue1: d.AttributeValue1,
        AttributeValue1Id: d.AttributeValue1Id,
        AttributeValue1Guid: d.AttributeValue1Guid,
        AttributeValue1Caption: d.AttributeValue1Caption,
        AttributeValue2: d.AttributeValue2,
        UUID: d.UUID,
      };
      const dynamicContentProperties =
        d.Properties && d.Properties.length > 0
          ? d.Properties.reduce((result, prop) => {
              result[prop.Name] = prop.Value;
              return result;
            }, {})
          : {};
      stages.forEach((stage) => {
        stage.sections
          .filter((section) => section.fields)
          .forEach((section) => {
            section.fields.forEach((field) => {
              dynamicContentItem[field.name] =
                field.type === "bit"
                  ? getBoolValue(dynamicContentProperties[field.name])
                  : dynamicContentProperties[field.name]
                  ? dynamicContentProperties[field.name]
                  : "";
            });
          });
      });
      dynamicContentItemsValuesArray.push(dynamicContentItem);
    });
  }

  //Default
  const propertyValues = {};
  stages.forEach((stage) => {
    stage.sections
      .filter((section) => section.fields)
      .forEach((section) => {
        section.fields.forEach((field) => {
          propertyValues[field.name] =
            field.type === "bit"
              ? getBoolValue(properties[field.name])
              : properties[field.name]
              ? properties[field.name]
              : "";
        });
      });
  });

  propertyValues.controlName = name;

  dispatch({
    type: DispatchMethods.SetReloadValues,
    reloadValues: {
      ...values,
      ...propertyValues,
      editControl: {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
        controlIndex: controlIndex,
        template: template,
      },
      uuid: control.UUID,
      attribute: control.Attribute,
      attributeName: control.AttributeName,
      attributeTarget: control.AttributeTarget,
      attributeType: control.AttributeType,
      dynamicContentItemsValuesArray: dynamicContentItemsValuesArray,
      entity: control.Entity,
      hideDefaultContent: control.HideDefaultContent,
    },
  });
};

export {
  applyControlChanges,
  cancelControlChanges,
  changeDesignMode,
  checkIfDynamicContentItemSelected,
  convertTypeNumberToEntityDisplayName,
  deleteControl,
  deleteRow,
  generateCommunicationTemplateDataAndHTML,
  generateUUID,
  getAttributeOperatorEnum,
  getDateString,
  getDynamicContentTitle,
  getLinkHref,
  getNameForTemplate,
  getPredefinedAttributes,
  getPredefinedAttributeDataTypeCodes,
  getPredefinedAttributesForDynamicContent,
  getPredefinedAttributeLookupEntityForDynamicContent,
  getPredefinedAttributeDataTypeCodesForDynamicContent,
  getStagesForControl,
  handleDragEnd,
  mapControlProperties,
  renderHeatMap,
  setCommCopyAttributes,
  setEditControl,
};
